import React, { Suspense, useEffect, useState } from "react";
import { getRouteApi } from "@tanstack/react-router";
import { Container } from "react-bootstrap";
import {
  MultiSelect,
  MultiSelectChangeEvent,
  MultiSelectFilterChangeEvent,
} from "@progress/kendo-react-dropdowns";
import {
  Controller,
  SubmitHandler,
  useController,
  useForm,
  useWatch,
} from "react-hook-form";
import { FiPlus, FiEdit } from "react-icons/fi";
import { MultiSelectToggleButton } from "@/components/shared/forms/MultiSelectToggleButton";
import { MultiSelectClearButton } from "@/components/shared/forms/MultiSelectClearButton";
import { multiSelectTagRender } from "@/components/shared/forms/multiSelectTagRender";
import { useMultiSelectChipListLabel } from "@/components/shared/forms/useMultiSelectChipListLabel";
import { useMultiSelectListboxMultiselectable } from "@/components/shared/forms/useMultiSelectListboxMultiselectable";
import { multiSelectSelectedItemRender } from "@/components/shared/forms/multiSelectSelectedItemRender";
import {
  LuMail,
  LuClipboardCheck,
  LuWrench,
  LuMegaphone,
  LuBold,
  LuUnderline,
  LuLink,
} from "react-icons/lu";
import { RiFileWarningFill } from "react-icons/ri";
import Popup from "@/components/shared/Popup";
import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import type { SystemMessage, MessageType, MessageStatus } from "@/models.ts";
import { renderRichText, visibleLength } from "./RichText";
import { useBodyEditor } from "./hooks/useBodyEditor";
import { deriveStatus, parseBackendDate } from "./MessageStatus";
import { useEntity, useChildEntities } from "@/queries";

const leaRoute = getRouteApi("/$leaid");

interface LeaOption {
  Text: string;
  Value: string;
}

interface SystemMessageFormPopupProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (message: SystemMessage) => void;
  initialMessage?: SystemMessage;
  saving?: boolean;
}

interface FormValues {
  messageType: MessageType;
  title: string;
  body: string;
  startDate: string;
  endDate: string;
  highPriority: boolean;
  global: boolean;
  leaIds: string[];
}

const MESSAGE_TYPE_OPTIONS = [
  { Text: "Certification", Value: "Certification" },
  { Text: "Maintenance", Value: "Maintenance" },
  { Text: "CAPSDAC Update", Value: "CAPSDAC Update" },
  { Text: "Action Required", Value: "Action Required" },
];

const TYPE_ICON_MAP: Record<MessageType, React.ReactNode> = {
  Certification: <LuClipboardCheck size={16} color="#003a5d" />,
  Maintenance: <LuWrench size={16} color="#475569" />,
  "CAPSDAC Update": <LuMegaphone size={16} color="#003a5d" />,
  "Action Required": <RiFileWarningFill size={16} color="#991b1b" />,
};

const TYPE_ICON_CLASS_MAP: Record<MessageType, string> = {
  Certification: "system-message-preview-icon--certification",
  Maintenance: "system-message-preview-icon--maintenance",
  "CAPSDAC Update": "system-message-preview-icon--update",
  "Action Required": "system-message-preview-icon--urgent",
};

const STATUS_BADGE_CLASS_MAP: Record<MessageStatus, string> = {
  Live: "system-message-status-badge--live",
  Scheduled: "system-message-status-badge--scheduled",
  Past: "system-message-status-badge--past",
};

const TITLE_MAX = 100;
const BODY_MAX = 300;
const FORM_ID = "frmSystemMessage";

const APP_TIMEZONE = "America/Los_Angeles";
const INPUT_SHAPE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

const PACIFIC_PARTS = new Intl.DateTimeFormat("en-US", {
  timeZone: APP_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

function pacificPartsOf(date: Date): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of PACIFIC_PARTS.formatToParts(date)) {
    if (p.type !== "literal") out[p.type] = p.value;
  }
  return out;
}

function backendIsoToPacificInput(iso: string | undefined | null): string {
  if (!iso) return "";
  const date = parseBackendDate(iso);
  if (isNaN(date.getTime())) return "";
  const p = pacificPartsOf(date);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

function pacificInputToDate(input: string): Date | null {
  const match = input?.match(INPUT_SHAPE);
  if (!match) return null;
  const [, y, m, d, h, mi] = match;

  const candidate = new Date(Date.UTC(+y, +m - 1, +d, +h, +mi));
  const p = pacificPartsOf(candidate);
  const pacificMs = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour),
    Number(p.minute),
    Number(p.second)
  );
  const offsetMs = candidate.getTime() - pacificMs;
  return new Date(candidate.getTime() + offsetMs);
}

function pacificInputToBackendIso(input: string): string {
  const utc = pacificInputToDate(input);
  return utc ? utc.toISOString() : "";
}

export default function SystemMessageFormPopup({
  show,
  onClose,
  onSubmit,
  initialMessage,
  saving = false,
}: SystemMessageFormPopupProps) {
  const { leaid } = leaRoute.useParams();

  const defaultValues: FormValues = {
    messageType: initialMessage?.messageType ?? "Certification",
    title: initialMessage?.title ?? "",
    body: initialMessage?.body ?? "",
    startDate: backendIsoToPacificInput(initialMessage?.startDate),
    endDate: backendIsoToPacificInput(initialMessage?.endDate),
    highPriority: initialMessage?.highPriority ?? false,
    global: initialMessage?.global ?? true,
    leaIds: initialMessage?.leaIds ?? [],
  };

  const {
    register,
    control,
    reset,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues,
    mode: "onSubmit",
  });

  useEffect(() => {
    if (show) reset(defaultValues);
  }, [show, initialMessage]);

  const watched = useWatch({ control });

  const bodyController = useController({
    name: "body",
    control,
    rules: {
      required: "Message body is required",
      validate: (value) =>
        visibleLength(value ?? "") <= BODY_MAX ||
        `Body must be ${BODY_MAX} characters or fewer (formatting markup excluded)`,
    },
  });
  const editor = useBodyEditor({
    value: bodyController.field.value ?? "",
    onChange: bodyController.field.onChange,
  });

  const handleBodyChange = (next: string) => {
    const prev = bodyController.field.value ?? "";
    const nextLen = visibleLength(next);
    if (nextLen > BODY_MAX && nextLen > visibleLength(prev)) {
      return;
    }
    bodyController.field.onChange(next);
  };

  const previewStatus = deriveStatus(
    pacificInputToDate(watched.startDate ?? ""),
    pacificInputToDate(watched.endDate ?? "")
  );

  const titleCount = (watched.title ?? "").length;
  const bodyCount = visibleLength(watched.body ?? "");

  const onValid: SubmitHandler<FormValues> = (values) => {
    const startIso = pacificInputToBackendIso(values.startDate);
    const endIso = pacificInputToBackendIso(values.endDate);

    if (startIso && endIso && endIso <= startIso) {
      setError("endDate", {
        type: "manual",
        message: "Display end must be after display start",
      });
      return;
    }

    if (!values.global && values.leaIds.length === 0) {
      setError("leaIds", {
        type: "manual",
        message: "Select at least one LEA, or switch back to Global",
      });
      return;
    }

    const leaIds = values.global ? undefined : values.leaIds;

    const result: SystemMessage = {
      id: initialMessage?.id ?? `msg-${Date.now()}`,
      messageType: values.messageType,
      title: values.title.trim(),
      body: values.body,
      highPriority: values.highPriority,
      global: values.global,
      leaIds,
      startDate: startIso,
      endDate: endIso,
      active: true,
      sortOrder: initialMessage?.sortOrder ?? 999,
    };

    onSubmit(result);
  };

  const header = (
    <div className="d-flex align-items-center gap-2 fs-5">
      {initialMessage ? <FiEdit size={20} /> : <FiPlus size={20} />}
      <span>
        {initialMessage ? "Edit System Message" : "New System Message"}
      </span>
    </div>
  );

  return (
    <Popup
      show={show}
      header={header}
      hasButton={false}
      enforceFocus={false}
      submitFormId={FORM_ID}
      submitButtonText={initialMessage ? "Save Changes" : "Create Message"}
      submitButtonLoading={isSubmitting || saving}
      submitButtonLoadingText="Saving..."
      secondButtonText="Cancel"
      size="lg"
      onClose={onClose}
      onSecondButtonClick={onClose}
    >
      <style>{`
        .system-message-toolbar {
          display: inline-flex;
          gap: 4px;
          margin-bottom: 8px;
        }

        .system-message-toolbar-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background-color: #ffffff;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          color: #475569;
          cursor: pointer;
          padding: 0;
          transition:
            background-color 0.12s ease,
            border-color 0.12s ease,
            color 0.12s ease;
        }

        .system-message-toolbar-btn:hover {
          background-color: #f8fafc;
          border-color: #94a3b8;
          color: #0f172a;
        }

        .system-message-toolbar-btn[aria-pressed="true"] {
          background-color: #e2e8f0;
          border-color: #94a3b8;
          color: #0f172a;
        }

        .system-message-toolbar-btn:active {
          background-color: #cbd5e1;
        }

        .system-message-toolbar-btn:focus-visible {
          outline: 2px solid var(--color-p3, #1e293b);
          outline-offset: -2px;
        }
      `}</style>

      <form
        id={FORM_ID}
        onSubmit={handleSubmit(onValid)}
        className="system-message-form"
      >
        <Container className="form-style overview-container ps-3 pe-3">
          {/* PREVIEW */}
          <div className="form-card-modern mb-3">
            <div className="section-header">
              <h5 className="section-title">Live Dashboard Preview</h5>
              <div className="ms-auto">
                <PreviewStatusBadge status={previewStatus} />
              </div>
            </div>

            <div className="card system-message-preview-card">
              <div className="system-message-preview-header">
                <LuMail size={16} color="#fff" />
                Important Messages
              </div>

              <PreviewBody
                messageType={watched.messageType ?? "Certification"}
                title={watched.title ?? ""}
                body={watched.body ?? ""}
              />
            </div>

            {!previewStatus && (
              <p className="system-message-preview-hint">
                Set Start and End dates to preview status.
              </p>
            )}
          </div>

          {/* IDENTIFICATION */}
          <div className="form-card-modern mb-3">
            <div className="section-header">
              <h5 className="section-title">Identification</h5>
            </div>

            <div className="row g-3">
              <div className="col-12">
                <FormFieldRow
                  name="messageType"
                  labelText="Message Label"
                  errors={errors}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id) => (
                    <Select
                      id={id}
                      {...register("messageType", {
                        required: "Message label is required",
                      })}
                      options={MESSAGE_TYPE_OPTIONS}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-12">
                <FormFieldRow
                  name="title"
                  labelText={`Title (${titleCount}/${TITLE_MAX})`}
                  errors={errors}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id) => (
                    <TextInput
                      id={id}
                      maxLength={TITLE_MAX}
                      placeholder="Shown as the message heading on user dashboards"
                      {...register("title", {
                        required: "Title is required",
                        maxLength: {
                          value: TITLE_MAX,
                          message: `Title must be ${TITLE_MAX} characters or fewer`,
                        },
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>
          </div>

          {/* CONTENT */}
          <div className="form-card-modern mb-3">
            <div className="section-header">
              <h5 className="section-title">Content</h5>
            </div>

            <div className="row g-3">
              <div className="col-12">
                <FormFieldRow
                  name="body"
                  labelText={`Message Body (${bodyCount}/${BODY_MAX})`}
                  errors={errors}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id) => (
                    <div>
                      <FormatToolbar
                        onBold={editor.bold}
                        onUnderline={editor.underline}
                        onLink={editor.openLink}
                        boldActive={editor.activeFormats.bold}
                        underlineActive={editor.activeFormats.underline}
                        linkActive={
                          editor.activeFormats.link != null ||
                          editor.linkDraft.open
                        }
                      />

                      {editor.linkDraft.open && (
                        <LinkDraftForm
                          text={editor.linkDraft.text}
                          url={editor.linkDraft.url}
                          error={editor.linkDraft.error}
                          onTextChange={editor.linkDraft.setText}
                          onUrlChange={editor.linkDraft.setUrl}
                          onInsert={editor.linkDraft.insert}
                          onCancel={editor.linkDraft.cancel}
                        />
                      )}

                      <textarea
                        id={id}
                        ref={editor.textareaRef}
                        className={`form-control system-message-body-textarea ${errors.body ? "is-invalid" : ""}`}
                        rows={5}
                        placeholder="Enter the message users will see. Use the toolbar above to add bold, underline, or links."
                        aria-required="true"
                        value={bodyController.field.value ?? ""}
                        onChange={(e) => handleBodyChange(e.target.value)}
                        onBlur={bodyController.field.onBlur}
                        onKeyDown={editor.handleKeyDown}
                        onSelect={editor.refreshSelection}
                        onKeyUp={editor.refreshSelection}
                      />
                    </div>
                  )}
                </FormFieldRow>
              </div>
            </div>
          </div>

          {/* SCHEDULE */}
          <div className="form-card-modern mb-3">
            <div className="section-header">
              <h5 className="section-title">
                Schedule{" "}
                <span
                  style={{
                    fontWeight: 400,
                    fontSize: "0.75rem",
                    color: "#64748b",
                    marginLeft: "0.25rem",
                  }}
                >
                  (Pacific Time)
                </span>
              </h5>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <FormFieldRow
                  name="startDate"
                  labelText="Display Start"
                  errors={errors}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id) => (
                    <Controller
                      name="startDate"
                      control={control}
                      rules={{ required: "Display start is required" }}
                      render={({ field }) => (
                        <input
                          id={id}
                          type="datetime-local"
                          className={`form-control ${errors.startDate ? "is-invalid" : ""}`}
                          aria-required="true"
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          onBlur={field.onBlur}
                        />
                      )}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-6">
                <FormFieldRow
                  name="endDate"
                  labelText="Display End"
                  errors={errors}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id) => (
                    <Controller
                      name="endDate"
                      control={control}
                      rules={{ required: "Display end is required" }}
                      render={({ field }) => (
                        <input
                          id={id}
                          type="datetime-local"
                          className={`form-control ${errors.endDate ? "is-invalid" : ""}`}
                          aria-required="true"
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          onBlur={field.onBlur}
                        />
                      )}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-12">
                <div className="form-check form-switch">
                  <input
                    id="highPriorityToggle"
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    {...register("highPriority")}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="highPriorityToggle"
                  >
                    High Priority — pin to the top of the message list
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* AUDIENCE */}
          <div className="form-card-modern mb-3">
            <div className="section-header">
              <h5 className="section-title">Audience</h5>
            </div>

            <div className="row g-3">
              <div className="col-12">
                <div className="form-check form-switch">
                  <input
                    id="globalToggle"
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    {...register("global")}
                  />
                  <label className="form-check-label" htmlFor="globalToggle">
                    Global — show to all LEAs
                  </label>
                </div>
              </div>

              {!watched.global && (
                <div className="col-12">
                  <FormFieldRow
                    name="leaIds"
                    labelText="LEAs"
                    errors={errors}
                    labelCol={12}
                    inputCol={12}
                  >
                    {(id) => (
                      <Controller
                        name="leaIds"
                        control={control}
                        render={({ field }) => (
                          <Suspense
                            fallback={
                              <div
                                className="text-muted"
                                style={{
                                  fontSize: "0.85rem",
                                  padding: "0.5rem 0",
                                }}
                              >
                                Loading LEAs…
                              </div>
                            }
                          >
                            <LeaPickerField
                              id={id}
                              leaid={leaid}
                              invalid={!!errors.leaIds}
                              value={field.value ?? []}
                              onChange={field.onChange}
                            />
                          </Suspense>
                        )}
                      />
                    )}
                  </FormFieldRow>
                </div>
              )}
            </div>
          </div>
        </Container>
      </form>
    </Popup>
  );
}

// =============================================================================
// View sub-components — kept inline because they're specific to this form.
// =============================================================================

function LeaPickerField({
  id,
  leaid,
  value,
  onChange,
  invalid = false,
}: {
  id?: string;
  leaid: string;
  value: string[];
  onChange: (ids: string[]) => void;
  invalid?: boolean;
}) {
  const entityResult = useEntity(leaid);
  const childrenResult = useChildEntities(leaid);

  const group = [entityResult?.data, ...(childrenResult?.data ?? [])]
    .flat()
    .filter(Boolean);
  const seen = new Set<string>();
  const options: LeaOption[] = [];
  for (const x of group as any[]) {
    const code = x?.CDSCode;
    if (!code) continue;
    const cdsCode = String(code);
    if (seen.has(cdsCode)) continue;
    seen.add(cdsCode);
    const name = x.LEAName ?? `LEA ${cdsCode}`;
    options.push({ Text: `${name} (${cdsCode})`, Value: cdsCode });
  }

  return (
    <LeaMultiSelect
      id={id}
      options={options}
      invalid={invalid}
      value={value}
      onChange={onChange}
    />
  );
}

function LeaMultiSelect({
  id,
  disabled = false,
  options,
  value,
  onChange,
  invalid = false,
  maxResults = 100,
}: {
  id?: string;
  disabled?: boolean;
  options: LeaOption[];
  value: string[];
  onChange: (ids: string[]) => void;
  invalid?: boolean;
  maxResults?: number;
}) {
  const [filter, setFilter] = useState("");
  // See MultiSelectToggleButton: gives keyboard users a way to browse the
  // LEA list without having to type first.
  const [opened, setOpened] = useState(false);

  const selected = value.map(
    (v) => options.find((o) => o.Value === v) ?? { Text: `LEA ${v}`, Value: v }
  );

  useMultiSelectChipListLabel(id, "LEAs", selected);
  useMultiSelectListboxMultiselectable(id, opened);

  const f = filter.trim().toLowerCase();
  const pool = f
    ? options.filter((o) => o.Text.toLowerCase().includes(f))
    : options;
  const data = pool.slice(0, maxResults);

  return (
    <MultiSelect
      id={id}
      data={data}
      textField="Text"
      dataItemKey="Value"
      value={selected}
      filterable
      onFilterChange={(e: MultiSelectFilterChangeEvent) =>
        setFilter(e.filter.value)
      }
      onChange={(e: MultiSelectChangeEvent) => {
        const next = (e.value as LeaOption[]) ?? [];
        onChange(next.map((o) => o.Value));
        setFilter("");
      }}
      disabled={disabled}
      style={{ width: "100%" }}
      placeholder="Search by LEA name or CDS code…"
      valid={!invalid}
      opened={opened}
      onOpen={() => setOpened(true)}
      onClose={() => setOpened(false)}
      itemRender={multiSelectSelectedItemRender}
      popupSettings={id ? { className: `${id}-listbox-popup` } : undefined}
      tagRender={multiSelectTagRender}
      suffix={
        <>
          <MultiSelectToggleButton
            opened={opened}
            onToggle={() => setOpened((prev) => !prev)}
            disabled={disabled}
          />
          {value.length > 0 && (
            <MultiSelectClearButton
              onClear={() => {
                onChange([]);
                setFilter("");
              }}
              disabled={disabled}
            />
          )}
        </>
      }
    />
  );
}

function FormatToolbar({
  onBold,
  onUnderline,
  onLink,
  boldActive,
  underlineActive,
  linkActive,
}: {
  onBold: () => void;
  onUnderline: () => void;
  onLink: () => void;
  boldActive: boolean;
  underlineActive: boolean;
  linkActive: boolean;
}) {
  return (
    <div
      role="toolbar"
      aria-label="Text formatting"
      className="system-message-toolbar"
    >
      <ToolbarButton
        ariaLabel="Bold (Ctrl/Cmd+B)"
        title="Bold (Ctrl/Cmd+B)"
        onClick={onBold}
        active={boldActive}
      >
        <LuBold size={16} aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        ariaLabel="Underline (Ctrl/Cmd+U)"
        title="Underline (Ctrl/Cmd+U)"
        onClick={onUnderline}
        active={underlineActive}
      >
        <LuUnderline size={16} aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        ariaLabel="Insert link (Ctrl/Cmd+K)"
        title="Insert link (Ctrl/Cmd+K)"
        onClick={onLink}
        active={linkActive}
      >
        <LuLink size={16} aria-hidden="true" />
      </ToolbarButton>
    </div>
  );
}

function ToolbarButton({
  children,
  onClick,
  ariaLabel,
  title,
  active,
}: {
  children: React.ReactNode;
  onClick: () => void;
  ariaLabel: string;
  title: string;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={title}
      aria-pressed={active}
      className="system-message-toolbar-btn"
    >
      {children}
    </button>
  );
}

function LinkDraftForm({
  text,
  url,
  error,
  onTextChange,
  onUrlChange,
  onInsert,
  onCancel,
}: {
  text: string;
  url: string;
  error: string | null;
  onTextChange: (v: string) => void;
  onUrlChange: (v: string) => void;
  onInsert: () => void;
  onCancel: () => void;
}) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onInsert();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
        padding: "0.75rem",
        marginBottom: "0.5rem",
        borderRadius: "0.375rem",
        border: "1px solid #cbd5e1",
        backgroundColor: "#f8fafc",
      }}
    >
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 180px" }}>
          <label
            htmlFor="linkDraftText"
            style={{
              display: "block",
              fontSize: "0.75rem",
              fontWeight: 500,
              color: "#475569",
              marginBottom: "0.25rem",
            }}
          >
            Link text
          </label>
          <input
            id="linkDraftText"
            type="text"
            className="form-control form-control-sm"
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="What the link will say"
            autoFocus
          />
        </div>
        <div style={{ flex: "2 1 240px" }}>
          <label
            htmlFor="linkDraftUrl"
            style={{
              display: "block",
              fontSize: "0.75rem",
              fontWeight: 500,
              color: "#475569",
              marginBottom: "0.25rem",
            }}
          >
            URL
          </label>
          <input
            id="linkDraftUrl"
            type="url"
            className="form-control form-control-sm"
            value={url}
            onChange={(e) => onUrlChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="https://example.com"
          />
        </div>
      </div>

      {error && (
        <div role="alert" style={{ color: "#b91c1c", fontSize: "0.75rem" }}>
          {error}
        </div>
      )}

      <div
        style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}
      >
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={onInsert}
        >
          Insert Link
        </button>
      </div>
    </div>
  );
}

function PreviewBody({
  messageType,
  title,
  body,
}: {
  messageType: MessageType;
  title: string;
  body: string;
}) {
  const icon = TYPE_ICON_MAP[messageType];
  const iconClass = TYPE_ICON_CLASS_MAP[messageType];

  const titleIsPlaceholder = !title;
  const bodyIsPlaceholder = !body;

  return (
    <div className="system-message-preview-row">
      <div className={`system-message-preview-icon ${iconClass}`}>{icon}</div>
      <div>
        <div
          className={`system-message-preview-title ${
            titleIsPlaceholder
              ? "system-message-preview-title--placeholder"
              : ""
          }`}
        >
          {title || "Title preview…"}
        </div>
        <div
          className={`system-message-preview-body ${
            bodyIsPlaceholder ? "system-message-preview-body--placeholder" : ""
          }`}
          style={{ whiteSpace: "pre-wrap" }}
        >
          {body ? renderRichText(body) : "Body preview…"}
        </div>
      </div>
    </div>
  );
}

function PreviewStatusBadge({ status }: { status: MessageStatus | null }) {
  const variantClass = status
    ? STATUS_BADGE_CLASS_MAP[status]
    : "system-message-status-badge--pending";

  const label = status ?? "Status pending";

  return (
    <span className={`system-message-status-badge ${variantClass}`}>
      {label}
    </span>
  );
}
